/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import dynamic from 'next/dynamic';
import clsx from 'clsx';
import qs from 'qs';
import { useRouter } from 'next/router';
import { DialogContent, DialogOverlay } from '@reach/dialog';
import { Loader, CurrencyPrice } from '@/components';
import Select, { components } from 'react-select';
import useWindowSize from '@/hooks/use-window-size';
import vars from '@/assets/styles/vars';

import styles from './ProductConfig.module.scss';

const SizeModal = dynamic(() => import('../sizeModal'), {
	loading: () => <Loader />,
});

const { Option, Menu } = components;

const customStyles = {
	valueContainer: provided => ({
		...provided,
		paddingLeft: 16,
		fontFamily: vars.fonts.futuraBook,
	}),
	control: provided => {
		return {
			...provided,
			flexWrap: 'nowrap',
			height: 44,
			borderRadius: 16,
		};
	},
	menu: (provided, state) => ({
		...provided,
		borderRadius: 16,
	}),
};

const CustomMenu = props => (
	<Menu {...props} className={styles.menuComponent}>
		{props.children}
	</Menu>
);

const CustomMenuList = props => {
	return (
		<div className={clsx(styles.scrollbar)}>
			<div>{props.children}</div>
		</div>
	);
};

const CustomMenuListModal = props => {
	return (
		<div className={clsx(styles.scrollbar, styles.scrollbarModal)}>
			<div>{props.children}</div>
		</div>
	);
};

const useDelayUnmount = (isMounted, delayTime) => {
	const [shouldRender, setShouldRender] = useState(false);

	useEffect(() => {
		let timeoutId;
		if (isMounted && !shouldRender) {
			setShouldRender(true);
		} else if (!isMounted && shouldRender) {
			timeoutId = setTimeout(() => setShouldRender(false), delayTime);
		}

		return () => clearTimeout(timeoutId);
	}, [isMounted, delayTime, shouldRender]);

	return shouldRender;
};

let touchStart = 0;
let touchDistance = 0;

const ProductOptions = ({
	options,
	setCurrentSlug,
	currentSize,
	setCurrentSize,
	isOpenSize,
	setIsOpenSize,
	sizes,
	noShowSizeTable,
	addToCart,
	addToWishlist,
	isLook,
	actualPrice,
	setActualPrice,
	setSizeLookModal,
	sizeChoosenModalState,
}) => {
	const router = useRouter();
	const { query } = router;
	const { isMobile } = useWindowSize();
	const { variant = null } = qs.parse(query);

	const selectRef = useRef();
	const dialogRef = useRef();
	const [openModal, setOpenModal] = useState(false);

	const CustomOptionSize = props => {
		return (
			<div
				onTouchEnd={() => {
					if (Math.abs(touchDistance) > 50) {
						touchDistance = 0;

						return;
					}

					touchDistance = 0;
					if (props.data.has) {
						handleInputChangeSize(props.data);
					}
				}}
				onTouchMove={e => {
					touchDistance = e.touches[0].clientY - touchStart;
				}}
				onTouchStart={e => {
					touchStart = e.touches[0].clientY;
				}}
			>
				<Option
					{...props}
					className={clsx(
						styles.sizeOption,
						currentSize && currentSize.value === props.data.value && styles.selectedValue,
						!props.data.has && styles.sizeOptionDisabled,
					)}
					disabled={!props.data.has}
				>
					<div>{props.data.label}</div>
					{props.data.price && props.data.has && (
						<div className={styles.sizeOptionPrice}>
							<CurrencyPrice price={props.data.price} isDiscount />
						</div>
					)}
				</Option>
			</div>
		);
	};

	const handleCloseModal = useCallback(() => {
		setOpenModal(false);
		if (!isLook) {
			selectRef.current?.blur();
		}
	}, [isLook]);

	const handleOpenModal = useCallback(() => {
		if (isMobile && (isLook || true)) {
			setOpenModal(true);
		}
	}, [isMobile]);

	const [currentOptions, setCurrentOptions] = useState([]);
	const theme = useMemo(() => {
		return {
			colors: {
				primary: '#59896D',
				neutral0: '#fff',
				neutral20: '#F0F0F0',
				neutral50: '#BBBBBB',
			},
		};
	}, []);

	const handleInputChangeSize = newValue => {
		setCurrentSize(newValue);
		setCurrentSlug(newValue.value);
		setSizeLookModal(newValue.value);
		setActualPrice(newValue?.price);

		if (isOpenSize) {
			addToCart(newValue);
		}

		if (openModal) {
			setIsOpenSize(false);
			handleCloseModal();
		}
	};

	const handleMenuClose = useCallback(() => {
		if (!openModal) {
			setIsOpenSize(false);
		}
	}, [setIsOpenSize, openModal]);

	useEffect(() => {
		if (options && options.length) {
			const newOptionsDate = options.map(({ has, price, variant_id, name }) => {
				const itemSize = {
					has,
					price: price != actualPrice ? price : null,
					value: variant_id,
					label: name,
					isDisabled: !has,
				};
				return itemSize;
			});
			setCurrentOptions(newOptionsDate);
			setCurrentSize(null);

			if (newOptionsDate.length === 1 && newOptionsDate[0].has) {
				handleInputChangeSize(newOptionsDate[0]);
			}
		}
	}, [options, variant, actualPrice]);

	useEffect(() => {
		if (variant && currentOptions && currentOptions.length) {
			currentOptions.map(option => {
				if (variant == option.value) {
					handleInputChangeSize(option);
				}
			});
		}
	}, [currentOptions, variant]);

	useEffect(() => {
		if (isLook && sizeChoosenModalState.length > 0 && currentOptions && currentOptions.length) {
			currentOptions.map(option => {
				if (sizeChoosenModalState.includes(option.value) && option.value !== currentSize?.value) {
					handleInputChangeSize(option);
				}
			});
		}
	}, [currentOptions, sizeChoosenModalState, isLook, currentSize]);

	useEffect(() => {
		if (isOpenSize && selectRef.current) {
			selectRef.current?.focus();
		}
	}, [isOpenSize]);

	const shouldRenderChild = useDelayUnmount(openModal, 300);
	const mountedStyle = { animation: 'inAnimation 300ms ease-in' };
	const unmountedStyle = { animation: 'outAnimation 300ms ease-in' };

	useEffect(() => {
		const handleRouteChange = url => {
			if (openModal) {
				setIsOpenSize(false);
				handleCloseModal();
			}
		};

		router.events.on('routeChangeStart', handleRouteChange);

		return () => {
			router.events.off('routeChangeStart', handleRouteChange);
		};
	}, [openModal]);

	useEffect(() => {
		const handleClickSearch = e => {
			if (openModal) {
				setIsOpenSize(false);
				handleCloseModal();
				setTimeout(() => {
					document?.getElementById('search-input')?.focus();
				}, 400);
			}
		};

		document.getElementById('header-id')?.addEventListener('click', handleClickSearch);

		return () => {
			document.getElementById('header-id')?.removeEventListener('click', handleClickSearch);
		};
	}, [openModal]);

	return (
		<>
			<Select
				className={styles.select}
				theme={theme}
				openMenuOnFocus={isMobile ? isOpenSize : true}
				value={currentSize}
				name='size'
				ref={selectRef}
				options={currentOptions}
				blurInputOnSelect
				placeholder='Выберите размер'
				onChange={handleInputChangeSize}
				onMenuClose={handleMenuClose}
				onMenuOpen={handleOpenModal}
				isSearchable={false}
				styles={customStyles}
				noOptionsMessage={() => 'Нет вариантов'}
				components={{
					IndicatorSeparator: null,
					Option: CustomOptionSize,
					Menu: CustomMenu,
					MenuList: isLook ? CustomMenuListModal : CustomMenuList,
				}}
			/>

			{shouldRenderChild && (
				<DialogOverlay
					isOpen={openModal}
					aria-label={undefined}
					className={clsx(styles.dialogRoot)}
					onDismiss={() => {
						if (openModal) {
							setIsOpenSize(false);
							handleCloseModal();
						}
					}}
				>
					<DialogContent
						className={clsx(styles.dialogContent)}
						style={openModal ? mountedStyle : unmountedStyle}
						aria-label={undefined}
						ref={dialogRef}
					>
						<div className={styles.dialogHeader}>
							<span className={styles.dialogHeaderTitle}>Выберите размер</span>
							<img
								onClick={() => {
									if (openModal) {
										setIsOpenSize(false);
										handleCloseModal();
									}
								}}
								className={styles.closeDialog}
								src='/images/svg/close.svg'
								alt='close'
							/>
						</div>
						<ul className={styles.dialogList}>
							{currentOptions.map(option => {
								return (
									<li
										className={clsx(
											styles.sizeOption,
											!option.has && styles.sizeOptionDisabled,
											currentSize?.value === option.value && styles.sizeOptionActive,
										)}
										disabled={!option.has}
										onClick={() => {
											if (option.has) {
												handleInputChangeSize(option);
											}
										}}
										onTouchEnd={() => {
											if (Math.abs(touchDistance) > 50) {
												touchDistance = 0;

												return;
											}

											touchDistance = 0;
											if (option.has) {
												handleInputChangeSize(option);
											}
										}}
										onTouchMove={e => {
											touchDistance = e.touches[0].clientY - touchStart;
										}}
										onTouchStart={e => {
											touchStart = e.touches[0].clientY;
										}}
										key={option.value}
									>
										<div>{option.label}</div>
										{option.has && option.price && option.price !== actualPrice && (
											<div className={styles.sizeOptionPrice}>
												<CurrencyPrice price={option.price} isDiscount />
											</div>
										)}
									</li>
								);
							})}
						</ul>
						{sizes && (
							<div className={styles.dialogSizeTable}>
								<SizeModal data={sizes} />
							</div>
						)}
					</DialogContent>
				</DialogOverlay>
			)}
		</>
	);
};

export default ProductOptions;
